import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

function Header() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setMenuOpen(false);
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/passeios?busca=${encodeURIComponent(searchTerm)}`);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const busca = params.get('busca');
    if (busca) {
      setSearchTerm(busca);
    }
  }, [location]);

  return (
    <header className={`bg-white text-gray-900 fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-xl' : 'shadow-md'}`}>
      <div className={`container mx-auto px-4 transition-all duration-300 ${scrolled ? 'py-3' : 'py-4'}`}>
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/logo-coop-igu.png"
              alt="COOP-IGU - Turismo • Receptivo • Eventos"
              className="h-12 md:h-16 w-auto object-contain"
              onError={(e) => {
                // Fallback caso a imagem não esteja disponível
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            {/* Fallback caso a imagem não esteja disponível */}
            <div className="flex items-center space-x-3 hidden">
              <span className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
                COOP-IGU
              </span>
              <span className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wider hidden sm:block">
                Turismo • Receptivo • Eventos
              </span>
            </div>
          </Link>

          {/* Barra de Busca Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-6">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t('header.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 pr-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </form>

          {/* Menu Desktop */}
          <nav className="hidden md:flex space-x-6 items-center">
            <button
              onClick={() => scrollToSection('inicio')}
              className="hover:text-orange-500 transition font-semibold text-gray-700"
            >
              {t('header.home')}
            </button>
            <Link
              to="/passeios"
              className="hover:text-orange-500 transition font-semibold text-gray-700"
            >
              {t('header.tours')}
            </Link>
            <button
              onClick={() => scrollToSection('sobre')}
              className="hover:text-orange-500 transition font-semibold text-gray-700"
            >
              {t('header.about')}
            </button>
                <button
                  onClick={() => scrollToSection('contato')}
                  className="hover:text-orange-500 transition font-semibold text-gray-700"
                >
                  {t('header.contact')}
                </button>
            <LanguageSelector />
            <Link
              to="/admin/login"
              className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-4 py-2 rounded-lg font-semibold hover:from-orange-600 hover:to-yellow-500 transition shadow-md hover:shadow-lg"
            >
              {t('header.admin')}
            </Link>
          </nav>

          {/* Menu Mobile Button */}
          <button
            className="md:hidden text-gray-900 focus:outline-none"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {menuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Menu Mobile */}
        {menuOpen && (
          <nav className="md:hidden mt-4 space-y-2 pb-4">
            {/* Busca Mobile */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('header.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 pr-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </form>
            <button
              onClick={() => scrollToSection('inicio')}
              className="block w-full text-left py-2 hover:text-orange-500 transition text-gray-700 font-semibold"
            >
              {t('header.home')}
            </button>
            <Link
              to="/passeios"
              onClick={() => setMenuOpen(false)}
              className="block w-full text-left py-2 hover:text-orange-500 transition text-gray-700 font-semibold"
            >
              {t('header.tours')}
            </Link>
            <button
              onClick={() => scrollToSection('sobre')}
              className="block w-full text-left py-2 hover:text-orange-500 transition text-gray-700 font-semibold"
            >
              {t('header.about')}
            </button>
                <button
                  onClick={() => scrollToSection('contato')}
                  className="block w-full text-left py-2 hover:text-orange-500 transition text-gray-700 font-semibold"
                >
                  {t('header.contact')}
                </button>
            <div className="my-2">
              <LanguageSelector />
            </div>
            <Link
              to="/admin/login"
              onClick={() => setMenuOpen(false)}
              className="block bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-4 py-2 rounded-lg font-semibold hover:from-orange-600 hover:to-yellow-500 transition mt-2"
            >
              {t('header.admin')}
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;

